/**
 * Hooks for adventure category management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adventureCategoriesApi } from '@api/endpoints/adventureCategories'
import type {
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '@api/types/adventureCategory'

// ===================
// QUERY HOOKS
// ===================

/**
 * Fetch all adventure categories
 */
export function useAdventureCategories(includeInactive: boolean = false) {
  return useQuery({
    queryKey: ['adventure-categories', { includeInactive }],
    queryFn: () => adventureCategoriesApi.getCategories({ includeInactive }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch a single category with full details
 */
export function useAdventureCategory(id: number | undefined) {
  return useQuery({
    queryKey: ['adventure-category', id],
    queryFn: () => adventureCategoriesApi.getCategory(id!),
    enabled: id !== undefined,
  })
}

/**
 * Fetch all user flags for dropdown
 */
export function useUserFlags() {
  return useQuery({
    queryKey: ['user-flags'],
    queryFn: () => adventureCategoriesApi.getUserFlags(),
    staleTime: 30 * 60 * 1000, // 30 minutes - these rarely change
  })
}

/**
 * Fetch all criteria types for multi-selects
 */
export function useCriteriaTypes() {
  return useQuery({
    queryKey: ['criteria-types'],
    queryFn: () => adventureCategoriesApi.getCriteriaTypes(),
    staleTime: 30 * 60 * 1000, // 30 minutes - these rarely change
  })
}

/**
 * Fetch all node types for multi-select
 */
export function useNodeTypes() {
  return useQuery({
    queryKey: ['node-types'],
    queryFn: () => adventureCategoriesApi.getNodeTypes(),
    staleTime: 30 * 60 * 1000, // 30 minutes - these rarely change
  })
}

// ===================
// MUTATION HOOKS
// ===================

/**
 * Create a new category
 */
export function useCreateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateCategoryRequest) =>
      adventureCategoriesApi.createCategory(data),
    onSuccess: () => {
      // Invalidate categories list
      queryClient.invalidateQueries({ queryKey: ['adventure-categories'] })
    },
  })
}

/**
 * Update an existing category
 */
export function useUpdateCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCategoryRequest }) =>
      adventureCategoriesApi.updateCategory(id, data),
    onSuccess: (_, variables) => {
      // Invalidate the specific category
      queryClient.invalidateQueries({ queryKey: ['adventure-category', variables.id] })
      // Invalidate categories list
      queryClient.invalidateQueries({ queryKey: ['adventure-categories'] })
    },
  })
}

/**
 * Delete a category
 */
export function useDeleteCategory() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) =>
      adventureCategoriesApi.deleteCategory(id),
    onSuccess: (_, id) => {
      // Invalidate the specific category
      queryClient.invalidateQueries({ queryKey: ['adventure-category', id] })
      // Invalidate categories list
      queryClient.invalidateQueries({ queryKey: ['adventure-categories'] })
    },
  })
}
